// AttachmentGrid — renders message attachments inside a bubble.
//
// Image attachments (png/jpg/gif/heic/svg/webp) render as 200px thumbnails in
// a horizontal strip. Click opens the Lightbox. Non-image attachments render
// as compact pills with filename + extension badge; clicking opens the file
// in a new tab.
//
// Attachments shape (from messages.attachments jsonb on the backend):
//   [{ id, filename, content_type, url, size_bytes }]
// Defensive: anything missing url silently drops.
//
// Polish #18 — when onMarkProof is provided (outbound bubbles only), each
// attachment gets a small "Mark as proof" affordance. Attachments that are
// already tracked as a proof show a "✓ Proof" badge instead and can't be
// re-marked. The parent (Center page) passes the set of proof-tracked
// attachment ids via `proofAttachmentIds` so we can render the badge.

import { useMemo, useState } from 'react';
import { Lightbox, type LightboxImage } from './Lightbox';

export interface Attachment {
  /** Attachment row id — required for Mark-as-proof wiring. Optional for
   *  legacy callers that only render and don't need the action. */
  id?: string;
  filename: string;
  content_type: string | null;
  url: string;
  size_bytes?: number | null;
}

const IMAGE_TYPES = /^image\//;
const IMAGE_EXT = /\.(png|jpe?g|gif|heic|webp|svg)$/i;

function isImage(a: Attachment): boolean {
  if (a.content_type && IMAGE_TYPES.test(a.content_type)) return true;
  if (IMAGE_EXT.test(a.filename)) return true;
  return false;
}

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes < 1024) return `${bytes ?? 0} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

interface AttachmentGridProps {
  attachments: Attachment[] | null | undefined;
  /**
   * When provided, renders a "Mark as proof" action on each attachment.
   * Parent (Center / ThreadView) only wires this on outbound bubbles.
   * Receives the attachment row id; pairs with the useMarkProof hook.
   */
  onMarkProof?: (attachmentId: string) => void;
  /** Attachment ids already tracked as a proof — renders a badge instead. */
  proofAttachmentIds?: Set<string>;
  /** When true, the mark-as-proof button shows a pending spinner state. */
  markProofPending?: boolean;
}

export function AttachmentGrid({
  attachments,
  onMarkProof,
  proofAttachmentIds,
  markProofPending,
}: AttachmentGridProps) {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  const valid = useMemo(
    () => (attachments ?? []).filter((a) => a && a.url && a.filename),
    [attachments],
  );
  const images = useMemo(() => valid.filter(isImage), [valid]);
  const files = useMemo(() => valid.filter((a) => !isImage(a)), [valid]);

  if (valid.length === 0) return null;

  const lightboxImages: LightboxImage[] = images.map((i) => ({
    url: i.url,
    alt: i.filename,
  }));

  const renderProofControl = (a: Attachment) => {
    if (!onMarkProof || !a.id) return null;
    const alreadyProof = proofAttachmentIds?.has(a.id) ?? false;
    if (alreadyProof) {
      return (
        <span className="msg-att-proof-badge" title="Tracked in the proof panel">
          ✓ Proof
        </span>
      );
    }
    return (
      <button
        type="button"
        className="msg-att-proof-btn"
        disabled={markProofPending}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onMarkProof(a.id!);
        }}
        title="Track this attachment as a proof in the right rail"
      >
        Mark as proof
      </button>
    );
  };

  return (
    <div className="msg-attachments">
      {images.length > 0 ? (
        <div className="msg-att-images">
          {images.map((img, i) => (
            <div key={img.url} className="msg-att-image-cell">
              <button
                type="button"
                className="msg-att-thumb"
                aria-label={`Open ${img.filename}`}
                onClick={() => setLightboxIdx(i)}
              >
                <img src={img.url} alt={img.filename} loading="lazy" />
              </button>
              {renderProofControl(img)}
            </div>
          ))}
        </div>
      ) : null}

      {files.length > 0 ? (
        <div className="msg-att-files">
          {files.map((f) => (
            <div key={f.url} className="msg-att-file-row">
              <a
                href={f.url}
                target="_blank"
                rel="noopener noreferrer"
                className="msg-att-pill"
              >
                <span className="msg-att-icon" aria-hidden>
                  📎
                </span>
                <span className="msg-att-name">{f.filename}</span>
                <span className="msg-att-size">{formatBytes(f.size_bytes)}</span>
              </a>
              {renderProofControl(f)}
            </div>
          ))}
        </div>
      ) : null}

      {lightboxIdx !== null && lightboxImages.length > 0 ? (
        <Lightbox
          images={lightboxImages}
          initialIndex={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      ) : null}
    </div>
  );
}
